import {
  DataModelAttribute,
  DataModelTypeEntity,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { ManifestInfo } from '@flowx/designer/shared/entities/application.entity'

/**
 * Token-budgeted view of a PROJECT/LIBRARY data-type catalogue handed to the AI so it can resolve a
 * generated data-model field against an existing project/library object. Descriptions, example
 * values, audit metadata and nested uuids are dropped; counts are capped.
 */
export type ProjectDataModelCatalogueAttribute = {
  name: string
  type: DataModelAttribute['type']
  multiInstance: boolean
  // For OBJECT attributes: the referenced data-type definition id (project) so nesting is traceable.
  reference?: string
}

export type ProjectDataModelCatalogueEntry = {
  resourceDefinitionId: string
  resourceId: string
  name: string
  appId: string
  appName: string
  isCurrentApp: boolean
  // A library object is a PROJECT data-type that belongs to a *different* app than the current one.
  isLibrary: boolean
  attributes: ProjectDataModelCatalogueAttribute[]
}

// Keep the catalogue inside a sane prompt budget.
const MAX_ENTRIES = 60
const MAX_ATTRIBUTES_PER_ENTRY = 30

const trimAttribute = (attribute: DataModelAttribute): ProjectDataModelCatalogueAttribute => ({
  name: attribute.name,
  type: attribute.type,
  multiInstance: !!attribute.multiInstance,
  ...(attribute.reference
    ? {
        reference:
          attribute.reference.targetResourceDefinitionId ||
          attribute.reference.targetFlowxUuid ||
          undefined,
      }
    : {}),
})

const manifestFor = (
  dataType: DataModelTypeEntity,
  manifestMap: Record<string, ManifestInfo>
): ManifestInfo | undefined => manifestMap[dataType.resourceId]

/**
 * Pure mapper: trim raw project-data-type entries (+ their manifest info) into a token-budgeted
 * catalogue. `currentApp` mirrors the isCurrentApp derivation from project-data-model.effects.ts.
 */
export const mapProjectDataModelToCatalogue = (
  dataTypes: DataModelTypeEntity[],
  manifestMap: Record<string, ManifestInfo>,
  currentApp: { appId: string; appVersionId: string }
): ProjectDataModelCatalogueEntry[] =>
  (dataTypes ?? []).slice(0, MAX_ENTRIES).map((dataType) => {
    const manifest = manifestFor(dataType, manifestMap)
    const isCurrentApp =
      !!manifest &&
      currentApp.appId === manifest.appId &&
      currentApp.appVersionId === manifest.appVersionId

    return {
      resourceDefinitionId: dataType.resourceDefinitionId,
      resourceId: dataType.resourceId,
      name: dataType.name,
      appId: manifest?.appId ?? currentApp.appId,
      appName: manifest?.appName ?? 'Local',
      isCurrentApp,
      isLibrary: !isCurrentApp,
      attributes: (dataType.attributes ?? []).slice(0, MAX_ATTRIBUTES_PER_ENTRY).map(trimAttribute),
    }
  })
